/* Custom Purchase Selector — CFP block
 *
 * - Two-tier selector: pack qty (1/2/3) + purchase type (subscribe / one-time)
 * - Reactive price display: any pack/type change recomputes all displayed
 *   prices using the base variant cents + subscribe discount %.
 * - ATC POSTs to /cart/add.js with the chosen quantity + selling_plan (only
 *   when subscribe is selected). On success, refreshes the custom cart
 *   drawer and opens it if the drawer is on the page.
 * - Falls back gracefully if Loop is missing / no plan attached (subscribe
 *   option hidden by liquid; JS just posts a plain qty add).
 */
(() => {
  if (window.__customPurchaseSelectorInit) return;
  window.__customPurchaseSelectorInit = true;

  const SEL = 'custom-purchase-selector';

  function formatMoneyCents(cents, format) {
    // Minimal Shopify-money-format renderer supporting the two most common
    // placeholders: {{amount}} (2 decimals) and {{amount_no_decimals}}.
    const num = (cents / 100).toFixed(2);
    const noDec = Math.round(cents / 100).toString();
    return (format || '${{amount}}')
      .replace(/{{\s*amount\s*}}/g, num)
      .replace(/{{\s*amount_no_decimals\s*}}/g, noDec)
      .replace(/{{\s*amount_with_comma_separator\s*}}/g, num.replace('.', ','))
      .replace(/{{\s*amount_no_decimals_with_comma_separator\s*}}/g, noDec);
  }

  function activePackEl(host) {
    return host.querySelector('.cfpps__pack.is-active')
        || host.querySelector('.cfpps__pack');
  }
  function currentPack(host) {
    const el = activePackEl(host);
    return el ? (parseInt(el.dataset.packQty, 10) || 1) : 1;
  }
  function currentSubDiscount(host) {
    const el = activePackEl(host);
    const perPack = el ? parseFloat(el.dataset.packSubDiscount) : 0;
    if (perPack && perPack > 0) return perPack;
    // Fallback to section-level
    return parseFloat(host.dataset.subDiscount) || 0;
  }
  function currentOtDiscount(host) {
    const el = activePackEl(host);
    return el ? (parseFloat(el.dataset.packOtDiscount) || 0) : 0;
  }
  function currentFreqMonths(host) {
    const el = activePackEl(host);
    return el ? (parseInt(el.dataset.packFreqMonths, 10) || 1) : 1;
  }

  function currentPtype(host) {
    const active = host.querySelector('.cfpps__ptype.is-active');
    if (active) return active.dataset.ptype;
    const checked = host.querySelector('.cfpps__radio-input:checked');
    if (checked) return checked.value;
    return 'ot';
  }

  function refreshPrices(host) {
    const baseCents = parseInt(host.dataset.baseCents, 10) || 0;
    const compareCents = parseInt(host.dataset.compareCents, 10) || 0;
    const unitsPerDay = parseFloat(host.dataset.unitsPerDay) || 30;
    const money = host.dataset.moneyFormat;
    const mode = host.dataset.priceMode || 'total'; // 'total' | 'per_pack'
    const freqTemplate = host.dataset.freqTemplate || 'Renews every [qty] mo.';

    const pack = currentPack(host);
    const subPct = currentSubDiscount(host);
    const otPct = currentOtDiscount(host);
    const freqMonths = currentFreqMonths(host);
    const freqDays = freqMonths * 30; // cheap approximation for $/day label

    // If the active pack has its own variant, use the variant's real prices
    // (Shopify + Loop as source of truth). Otherwise fall back to the legacy
    // "base × qty × discount%" math so old configs keep working.
    const packEl = activePackEl(host);
    const packVariantPrice = packEl ? parseInt(packEl.dataset.packVariantPrice, 10) : 0;
    const packVariantCompare = packEl ? parseInt(packEl.dataset.packVariantCompare, 10) : 0;
    const packVariantSubPrice = packEl ? parseInt(packEl.dataset.packVariantSubPrice, 10) : 0;
    const hasVariant = packVariantPrice > 0;

    let otTotalCents;
    let subTotalCents;
    let otCompareTotalCents;
    let subCompareTotalCents;

    if (hasVariant) {
      // Variant-switch mode — Shopify variant is the source of truth.
      otTotalCents = packVariantPrice;
      subTotalCents = packVariantSubPrice > 0
        ? packVariantSubPrice
        : Math.round(packVariantPrice * (1 - subPct / 100));
      // Compare-at only shows if it's higher than the discounted display.
      otCompareTotalCents = packVariantCompare > 0 ? packVariantCompare : 0;
      subCompareTotalCents = packVariantCompare > 0 ? packVariantCompare : 0;
    } else {
      // Legacy quantity-multiplier mode.
      // Rounding rules differ between the two discount sources:
      //   • Shopify automatic percentage discounts (one-time) round the
      //     per-item discount DOWN (floor) — so 20% × $49.99 = $9.998 →
      //     $9.99 discount, price becomes $40.00/unit.
      //   • Loop selling-plan percentage discounts (subscribe) round
      //     the per-item discount to the nearest cent (round) — so
      //     15% × $39.99 = $5.9985 → $6.00 discount, price → $33.99/unit.
      // Applying the wrong rule drifts a cent or two per pack vs what
      // Shopify actually charges at checkout.
      const otDiscountPerUnit = Math.floor((baseCents * otPct) / 100);
      const subDiscountPerUnit = Math.round((baseCents * subPct) / 100);
      const otPerPack = baseCents - otDiscountPerUnit;
      const subPerPack = baseCents - subDiscountPerUnit;
      otTotalCents = otPerPack * pack;
      subTotalCents = subPerPack * pack;
      const compareBase = compareCents > 0 ? compareCents : baseCents;
      otCompareTotalCents = compareBase * pack;
      subCompareTotalCents = otCompareTotalCents;
    }

    // Per-pack display = total ÷ pack qty; total display = raw total.
    const otDisplayCents = mode === 'per_pack' ? Math.round(otTotalCents / pack) : otTotalCents;
    const subDisplayCents = mode === 'per_pack' ? Math.round(subTotalCents / pack) : subTotalCents;
    const otCompareCents = mode === 'per_pack' ? Math.round(otCompareTotalCents / pack) : otCompareTotalCents;
    const subCompareCents = mode === 'per_pack' ? Math.round(subCompareTotalCents / pack) : subCompareTotalCents;

    // $/day is always based on totals regardless of display mode.
    const daysCovered = pack * unitsPerDay || 1;
    const otPerDay = otTotalCents / daysCovered / 100;
    const subPerDay = subTotalCents / daysCovered / 100;

    // Subscribe card
    const subPrice = host.querySelector('[data-ps-price-current]');
    const subCompare = host.querySelector('[data-ps-price-compare]');
    const subPerDayEl = host.querySelector('[data-ps-perday]');
    const subFreqEl = host.querySelector('[data-ps-sub-freq]');
    if (subPrice) subPrice.textContent = formatMoneyCents(subDisplayCents, money);
    if (subCompare) {
      if (subCompareCents > subDisplayCents) {
        subCompare.textContent = formatMoneyCents(subCompareCents, money);
        subCompare.style.display = '';
      } else {
        subCompare.textContent = '';
        subCompare.style.display = 'none';
      }
    }
    // Under-price line: user-editable "Price unit label" (e.g. "/BOTTLE")
    // overrides the auto "$X.XX/day" when set.
    const priceUnitLabel = (host.dataset.priceUnitLabel || '').trim();
    if (subPerDayEl) {
      subPerDayEl.textContent = priceUnitLabel || ('$' + subPerDay.toFixed(2) + '/day');
    }
    // Replace both [qty] (new token) and legacy {n} (older configs) so existing
    // saved settings keep working without a manual re-edit.
    // Total units in the selected pack (e.g. 30 strips/pack × 2 packs = 60).
    // Same number is also the days-covered when 1 unit = 1 day.
    const totalUnits = Math.round(pack * unitsPerDay);
    const applyFreqTokens = (str, monthsToken) => (str || '')
      .replace(/\[qty\]/g, monthsToken)
      .replace(/\{n\}/g, monthsToken)
      .replace(/\[units\]/g, totalUnits)
      .replace(/\[days\]/g, totalUnits)
      .replace(/\[packs\]/g, pack);

    if (subFreqEl) {
      subFreqEl.textContent = applyFreqTokens(freqTemplate, freqMonths);
    }

    // Subscribe card title supports [qty] token so it can read
    // "1-Month Delivery" / "2-Month Delivery" / "3-Month Delivery" per
    // active pack. All standard freq tokens work here too.
    const subTitleEl = host.querySelector('[data-ps-sub-title]');
    if (subTitleEl) {
      const titleTpl = host.dataset.subTitleTemplate || '';
      if (titleTpl) subTitleEl.textContent = applyFreqTokens(titleTpl, freqMonths);
    }

    // Second-line "Delivered every X months" — separate template for
    // singular (1 month) vs plural (2+ months) so grammar stays right.
    const subDeliveryEl = host.querySelector('[data-ps-sub-delivery]');
    if (subDeliveryEl) {
      const singular = host.dataset.subDeliverySingular || '';
      const plural = host.dataset.subDeliveryPlural || '';
      const template = freqMonths === 1 ? singular : plural;
      const text = template ? applyFreqTokens(template, freqMonths) : '';
      subDeliveryEl.textContent = text;
      subDeliveryEl.style.display = text ? '' : 'none';
    }

    // One-time frequency line also updates per pack — "[units] strips
    // delivered once" turns into 30 / 60 / 90 strips as pack changes.
    const otFreqEl = host.querySelector('[data-ps-ot-freq]');
    if (otFreqEl) {
      const otFreqTemplate = host.dataset.otFreqTemplate || '';
      // For one-time there's no renewal frequency — always pass pack qty
      // as the "months" token in case someone uses [qty] there.
      otFreqEl.textContent = applyFreqTokens(otFreqTemplate, pack);
    }

    // Per-pack shipping bullets:
    //   Subscribe card: shows a "Free shipping" bullet only when active
    //     pack has data-pack-sub-shipping="1".
    //   One-time card: shows either the "Free" or "No free" variant, or
    //     hides both, based on active pack's data-pack-ot-shipping value.
    const subShipBullet = host.querySelector('[data-ps-sub-shipping]');
    if (subShipBullet && packEl) {
      subShipBullet.style.display = packEl.dataset.packSubShipping === '1' ? '' : 'none';
    }
    const otFreeBullet = host.querySelector('[data-ps-ot-shipping-free]');
    const otPaidBullet = host.querySelector('[data-ps-ot-shipping-paid]');
    const otShipState = packEl ? (packEl.dataset.packOtShipping || 'free') : 'free';
    if (otFreeBullet) otFreeBullet.style.display = otShipState === 'free' ? '' : 'none';
    if (otPaidBullet) otPaidBullet.style.display = otShipState === 'paid' ? '' : 'none';

    // One-time card
    const otPrice = host.querySelector('[data-ps-ot-price]');
    const otCompare = host.querySelector('[data-ps-ot-price-compare]');
    const otPerDayEl = host.querySelector('[data-ps-ot-perday]');
    if (otPrice) otPrice.textContent = formatMoneyCents(otDisplayCents, money);
    if (otCompare) {
      if (otCompareCents > otDisplayCents) {
        otCompare.textContent = formatMoneyCents(otCompareCents, money);
        otCompare.style.display = '';
      } else {
        otCompare.textContent = '';
        otCompare.style.display = 'none';
      }
    }
    if (otPerDayEl) {
      otPerDayEl.textContent = priceUnitLabel || ('$' + otPerDay.toFixed(2) + '/day');
    }

    // CTA button always shows the TOTAL that will hit the cart (regardless
    // of card display mode). Which total depends on which purchase type is
    // selected right now.
    const ptypeNow = currentPtype(host);

    // Per-pack SAVE label swaps between the one-time and subscribe texts
    // set in the customizer. Blank = hide (no fallback across purchase
    // types) — so a pack with no OT discount can leave OT blank and no
    // SAVE pill shows when One-time is selected, even if a Subscribe
    // SAVE text is set.
    host.querySelectorAll('[data-ps-pack-save]').forEach((el) => {
      const text = (ptypeNow === 'sub' ? el.dataset.saveSub : el.dataset.saveOt) || '';
      el.textContent = text;
      el.style.display = text ? '' : 'none';
    });
    const ctaPrice = host.querySelector('[data-ps-cta-price]');
    const ctaCompare = host.querySelector('[data-ps-cta-compare]');
    const ctaTotal = ptypeNow === 'sub' ? subTotalCents : otTotalCents;
    const ctaCompareTotal = ptypeNow === 'sub' ? subCompareTotalCents : otCompareTotalCents;
    if (ctaPrice) ctaPrice.textContent = formatMoneyCents(ctaTotal, money);
    if (ctaCompare) {
      if (ctaCompareTotal > ctaTotal) {
        ctaCompare.textContent = formatMoneyCents(ctaCompareTotal, money);
        ctaCompare.style.display = '';
      } else {
        ctaCompare.textContent = '';
        ctaCompare.style.display = 'none';
      }
    }
  }

  function setActive(container, selector, matchFn) {
    container.querySelectorAll(selector).forEach((el) => {
      el.classList.toggle('is-active', matchFn(el));
      if (el.getAttribute('role') === 'radio') {
        el.setAttribute('aria-checked', matchFn(el) ? 'true' : 'false');
      }
    });
  }

  function showError(host, msg) {
    let box = host.querySelector('[data-ps-error]');
    if (!box) {
      box = document.createElement('div');
      box.dataset.psError = '';
      box.className = 'cfpps__error';
      const cta = host.querySelector('[data-ps-cta]');
      if (cta && cta.parentNode) cta.parentNode.insertBefore(box, cta.nextSibling);
    }
    box.textContent = msg;
    box.style.display = '';
  }
  function clearError(host) {
    const box = host.querySelector('[data-ps-error]');
    if (box) { box.textContent = ''; box.style.display = 'none'; }
  }

  async function addToCart(host, btn) {
    clearError(host);
    const ptype = currentPtype(host);
    const packEl = activePackEl(host);

    // Prefer the active pack's own variant when set — variant-switch flow
    // (each pack maps to a distinct Shopify variant, qty=1). Falls back to
    // the section-level variant × pack qty when the pack has no variant_id.
    const packVariantId = packEl ? parseInt(packEl.dataset.packVariantId, 10) : 0;
    let variantId, qty;
    if (packVariantId > 0) {
      variantId = packVariantId;
      qty = 1;
    } else {
      variantId = parseInt(host.dataset.variantId, 10);
      qty = currentPack(host);
    }
    if (!variantId) {
      console.warn('[purchase-selector] no variant id on host', host);
      showError(host, 'No product variant configured for this block.');
      return;
    }

    // Selling plan: pack-level override wins, else section-level.
    let sellingPlan = null;
    if (ptype === 'sub') {
      const perPackPlan = packEl && parseInt(packEl.dataset.packSellingPlanId, 10);
      sellingPlan = perPackPlan
        || parseInt(host.dataset.subPlanId, 10)
        || null;
    }

    // Shopify prefers the `items` array form; also carries selling_plan reliably.
    const item = { id: variantId, quantity: qty };
    if (sellingPlan) item.selling_plan = sellingPlan;
    const body = { items: [item] };

    console.log('[purchase-selector] POST /cart/add.js', body);

    if (btn) {
      btn.disabled = true;
      btn.classList.add('is-loading');
    }
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      console.log('[purchase-selector] /cart/add.js →', res.status, json);
      if (!res.ok) {
        const msg = (json && (json.description || json.message)) || `Add to cart failed (${res.status})`;
        showError(host, msg);
        return;
      }
    } catch (err) {
      console.warn('[purchase-selector] add failed:', err);
      showError(host, 'Network error — could not add to cart. Please try again.');
      return;
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      }
    }
    // Post-add action — either open the drawer (default) or skip
    // straight to /checkout. Controlled by the block's "After add to
    // cart" select.
    const action = (host.dataset.ctaAction || 'drawer').trim();
    if (action === 'checkout') {
      window.location.href = window.Shopify?.routes?.root
        ? window.Shopify.routes.root.replace(/\/$/, '') + '/checkout'
        : '/checkout';
      return;
    }
    // Default: refresh + open the custom cart drawer if present.
    if (window.CustomCartDrawer) {
      try {
        await window.CustomCartDrawer.refresh();
        window.CustomCartDrawer.open();
      } catch (_) { /* drawer not ready — ignore */ }
    }
  }

  function initHost(host) {
    if (host.__psInit) return;
    host.__psInit = true;

    // Pack click
    host.querySelectorAll('.cfpps__pack').forEach((btn) => {
      btn.addEventListener('click', () => {
        setActive(host, '.cfpps__pack', (el) => el === btn);
        refreshPrices(host);
      });
    });

    // Purchase-type click (label wraps radio, but we want to react to any
    // click inside the card, not just the radio input itself)
    host.querySelectorAll('.cfpps__ptype').forEach((card) => {
      card.addEventListener('click', () => {
        const radio = card.querySelector('.cfpps__radio-input');
        if (radio) radio.checked = true;
        setActive(host, '.cfpps__ptype', (el) => el === card);
        refreshPrices(host);
      });
    });

    // ATC
    const cta = host.querySelector('[data-ps-cta]');
    if (cta) {
      cta.addEventListener('click', (e) => {
        e.preventDefault();
        addToCart(host, cta);
      });
    }

    // Initial paint
    refreshPrices(host);
  }

  function initAll() {
    document.querySelectorAll(SEL).forEach(initHost);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init when Shopify reloads the CFP section in the customizer.
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelector) {
      e.target.querySelectorAll(SEL).forEach(initHost);
    }
  });
})();

if (!customElements.get('custom-purchase-selector')) {
  customElements.define('custom-purchase-selector', class extends HTMLElement {});
}
